import {
  collection, doc, setDoc, getDoc, getDocs, deleteDoc, query, orderBy, limit, serverTimestamp, Timestamp,
} from 'firebase/firestore';
import { ref, uploadString, getBlob, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase.js';
import { createIdempotencyKey, runClientIdempotent } from './idempotency.js';

// ── Chat history lifecycle (see README "Storage & size limits") ─────────
//
// - Live window: last WINDOW_SIZE messages as Firestore docs
//   (topics/{topicId}/messages/{messageId}), one doc per message — never
//   an array field, so a long conversation can never overflow the 1 MiB
//   Firestore document cap.
// - Every message doc is stamped with expiresAt = now + TTL_DAYS days.
//   Enable a Firestore TTL policy on `expiresAt` (see README) so abandoned
//   topics self-clean for free even if the app never runs eviction.
// - On write, if the live window exceeds WINDOW_SIZE, the oldest messages
//   are appended to a per-topic archive file in Storage
//   (chatArchive.json) and their Firestore docs are deleted. Storage has
//   no per-doc overhead, so archiving is far cheaper than growing Firestore.
// - When the LLM needs context, we only ever send the last ~6 messages —
//   the generated HTML file *is* the real state; older chat has no
//   functional value to generation, only to the human scrollback.

const WINDOW_SIZE = 20;
const CONTEXT_MESSAGES = 6;
const TTL_DAYS = 60;

const messagesCol = (uid, courseId, topicId) =>
  collection(db, 'users', uid, 'courses', courseId, 'topics', topicId, 'messages');

function archivePath(uid, courseId, topicId) {
  return `userFiles/${uid}/${courseId}/${topicId}/chatArchive.json`;
}

export async function appendMessage(uid, courseId, topicId, {
  role, text, idempotencyKey = createIdempotencyKey('appendMessage'),
}) {
  return runClientIdempotent({
    uid,
    operation: 'appendMessage',
    key: idempotencyKey,
    payload: { courseId, topicId, role, text },
    handler: async () => {
      const expiresAt = Timestamp.fromMillis(Date.now() + TTL_DAYS * 24 * 60 * 60 * 1000);
      const ref = doc(messagesCol(uid, courseId, topicId));
      await setDoc(ref, {
        role, text, createdAt: serverTimestamp(), expiresAt, idempotencyKey,
      });
      await evictIfNeeded(uid, courseId, topicId);
      return ref.id;
    },
  });
}

// Deterministic id for one generation turn's assistant message — MUST match
// functions/topicPersistence.js's upsertAssistantMessage (`${jobId}-assistant`,
// where jobId is this same generationSessionId — see src/lib/generate.js).
// The server always writes a plain "Generated version N." message for this
// turn the moment it finishes saving (so a disconnected/unattended
// generation still leaves a real chat entry); this lets studio.js overwrite
// that SAME doc with the fuller "what changed / what may have regressed"
// text (src/lib/pageDiff.js describeChangeReport) once its own
// browser-only validation pass (sandbox.js probePage) finishes, instead of
// creating a second, duplicate message for the same turn.
export function assistantMessageId(sessionId) {
  return `${sessionId}-assistant`;
}

export async function upsertAssistantMessage(uid, courseId, topicId, sessionId, text) {
  if (!sessionId || !text) return;
  const expiresAt = Timestamp.fromMillis(Date.now() + TTL_DAYS * 24 * 60 * 60 * 1000);
  await setDoc(doc(messagesCol(uid, courseId, topicId), assistantMessageId(sessionId)), {
    role: 'assistant', text, createdAt: serverTimestamp(), expiresAt,
  }, { merge: true });
  await evictIfNeeded(uid, courseId, topicId);
}

// One-time lookup of a single turn's own assistant message by its
// deterministic id (see assistantMessageId above) — used right after a
// stream read fails, to check whether the server actually finished and
// wrote this turn's reply before treating it as a failure. Every server
// path that answers a turn (a real build, a chat-driven edit, or a plain
// chat answer — see functions/pluginAgentHandler.js) writes this exact doc
// the moment it finishes, independent of whether the browser is still
// connected, so its existence is a reliable "this turn is already done"
// signal regardless of which of those three paths handled it.
export async function getAssistantMessage(uid, courseId, topicId, sessionId) {
  if (!sessionId) return null;
  const snap = await getDoc(doc(messagesCol(uid, courseId, topicId), assistantMessageId(sessionId)));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function loadLiveMessages(uid, courseId, topicId) {
  const q = query(messagesCol(uid, courseId, topicId), orderBy('createdAt', 'asc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// Returns just the tail used as LLM context — cheap, no archive read.
export async function loadContextMessages(uid, courseId, topicId) {
  const q = query(messagesCol(uid, courseId, topicId), orderBy('createdAt', 'desc'), limit(CONTEXT_MESSAGES));
  const snap = await getDocs(q);
  return snap.docs.map((d) => d.data()).reverse();
}

// Lazily loads the archive so scrolling above the live window is invisible
// to the user — they just see older messages appear.
export async function loadArchivedMessages(uid, courseId, topicId) {
  try {
    const blob = await getBlob(ref(storage, archivePath(uid, courseId, topicId)));
    const text = await blob.text();
    return JSON.parse(text);
  } catch (e) {
    return []; // no archive yet
  }
}

async function evictIfNeeded(uid, courseId, topicId) {
  const q = query(messagesCol(uid, courseId, topicId), orderBy('createdAt', 'asc'));
  const snap = await getDocs(q);
  if (snap.docs.length <= WINDOW_SIZE) return;

  const overflow = snap.docs.length - WINDOW_SIZE;
  const toEvict = snap.docs.slice(0, overflow);
  const evictedPayload = toEvict.map((d) => {
    const data = d.data();
    return {
      role: data.role,
      text: data.text,
      createdAt: data.createdAt?.toMillis?.() ?? null,
    };
  });

  const existing = await loadArchivedMessages(uid, courseId, topicId);
  const merged = existing.concat(evictedPayload);
  await uploadString(ref(storage, archivePath(uid, courseId, topicId)), JSON.stringify(merged), 'raw', {
    contentType: 'application/json',
  });

  await Promise.all(toEvict.map((d) => deleteDoc(d.ref)));
}

// "Clear chat" — wipes live docs + archive. The generated page/version
// history is untouched.
export async function clearChat(uid, courseId, topicId) {
  const snap = await getDocs(messagesCol(uid, courseId, topicId));
  await Promise.all(snap.docs.map((d) => deleteDoc(d.ref)));
  await deleteObject(ref(storage, archivePath(uid, courseId, topicId))).catch(() => {});
}
